import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend,
  ScatterChart, Scatter, LineChart, Line
} from 'recharts';
import { 
  Activity, TrendingUp, TrendingDown, Users, 
  Euro, Target, Award, AlertCircle, Star
} from "lucide-react";

interface ActivityProfitabilityProps {
  period: string;
}

const COLORS = {
  primary: '#3b82f6',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#06b6d4',
  purple: '#8b5cf6'
};

// Datos de ejemplo - en producción vendrían de la base de datos
const activityData = [
  {
    name: 'Fútbol',
    revenue: 1200,
    students: 24,
    sessions: 96,
    revenuePerStudent: 50,
    revenuePerSession: 12.5,
    occupancyRate: 80,
    profitMargin: 75,
    trend: 'up',
    growth: 12.5
  },
  {
    name: 'Baloncesto',
    revenue: 800,
    students: 16,
    sessions: 64,
    revenuePerStudent: 50,
    revenuePerSession: 12.5,
    occupancyRate: 67,
    profitMargin: 70,
    trend: 'up',
    growth: 8.3
  },
  {
    name: 'Natación',
    revenue: 600,
    students: 12,
    sessions: 48,
    revenuePerStudent: 50,
    revenuePerSession: 12.5,
    occupancyRate: 50,
    profitMargin: 80,
    trend: 'down',
    growth: -5.2
  },
  {
    name: 'Tenis',
    revenue: 450,
    students: 9,
    sessions: 36,
    revenuePerStudent: 50,
    revenuePerSession: 12.5,
    occupancyRate: 45,
    profitMargin: 85,
    trend: 'stable',
    growth: 0.8
  },
  {
    name: 'Padel',
    revenue: 380,
    students: 8,
    sessions: 32,
    revenuePerStudent: 47.5,
    revenuePerSession: 11.9,
    occupancyRate: 40,
    profitMargin: 72,
    trend: 'up',
    growth: 15.7
  }
];

const profitabilityMetrics = [
  { name: 'Ingresos', value: 3430, change: 8.5, isGood: true },
  { name: 'Estudiantes', value: 69, change: 12.3, isGood: true },
  { name: 'Sesiones', value: 276, change: 6.2, isGood: true },
  { name: 'Ocupación Media', value: 56.4, change: 4.1, isGood: true }
];

const radarData = activityData.map(activity => ({
  activity: activity.name,
  ingresos: (activity.revenue / 1200) * 100,
  ocupacion: activity.occupancyRate,
  margen: activity.profitMargin,
  estudiantes: (activity.students / 24) * 100,
  crecimiento: Math.max(0, (activity.growth + 10) * 5) // Normalizar para radar
}));

export const ActivityProfitability = ({ period }: ActivityProfitabilityProps) => {
  const totalRevenue = activityData.reduce((sum, activity) => sum + activity.revenue, 0);
  const totalStudents = activityData.reduce((sum, activity) => sum + activity.students, 0);
  const avgOccupancy = activityData.reduce((sum, activity) => sum + activity.occupancyRate, 0) / activityData.length;
  const mostProfitable = activityData.reduce((max, activity) => 
    activity.profitMargin > max.profitMargin ? activity : max
  );

  return (
    <div className="space-y-6">
      {/* KPIs de rentabilidad */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Actividad Más Rentable
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-lg font-bold text-success">
                  {mostProfitable.name}
                </div>
                <div className="text-sm text-muted-foreground">
                  {mostProfitable.profitMargin}% margen
                </div>
              </div>
              <Award className="w-8 h-8 text-warning" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Ingresos Totales
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-primary">
                  €{totalRevenue.toLocaleString()}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="w-3 h-3 text-success" />
                  <span className="text-xs text-success">+8.5%</span>
                </div>
              </div>
              <Euro className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Estudiantes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-info">
                  {totalStudents}
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="w-3 h-3 text-success" />
                  <span className="text-xs text-success">+12.3%</span>
                </div>
              </div>
              <Users className="w-8 h-8 text-info" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Ocupación Media
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-warning">
                  {avgOccupancy.toFixed(1)}%
                </div>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="w-3 h-3 text-success" />
                  <span className="text-xs text-success">+4.1%</span>
                </div>
              </div>
              <Target className="w-8 h-8 text-warning" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos principales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ingresos por actividad */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Euro className="w-5 h-5 text-primary" />
              Ingresos por Actividad
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={activityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value) => `€${value}`} />
                <Bar dataKey="revenue" fill={COLORS.primary} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Análisis radar */}
        <Card className="shadow-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Análisis Multidimensional
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="activity" />
                <PolarRadiusAxis angle={90} domain={[0, 100]} />
                <Radar
                  name="Ingresos"
                  dataKey="ingresos"
                  stroke={COLORS.primary}
                  fill={COLORS.primary}
                  fillOpacity={0.1}
                />
                <Radar
                  name="Ocupación"
                  dataKey="ocupacion"
                  stroke={COLORS.success}
                  fill={COLORS.success}
                  fillOpacity={0.1}
                />
                <Radar
                  name="Margen"
                  dataKey="margen"
                  stroke={COLORS.warning}
                  fill={COLORS.warning}
                  fillOpacity={0.1}
                />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Análisis detallado por actividad */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Análisis Detallado por Actividad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {activityData.map((activity, index) => (
              <div key={activity.name} className="p-4 border rounded-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <Activity className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{activity.name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {activity.trend === 'up' ? (
                          <TrendingUp className="w-4 h-4 text-success" />
                        ) : activity.trend === 'down' ? (
                          <TrendingDown className="w-4 h-4 text-danger" />
                        ) : (
                          <Activity className="w-4 h-4 text-gray-500" />
                        )}
                        <span className={`text-sm font-medium ${
                          activity.trend === 'up' ? 'text-success' : 
                          activity.trend === 'down' ? 'text-danger' : 
                          'text-gray-500'
                        }`}>
                          {activity.growth >= 0 ? '+' : ''}{activity.growth}%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">€{activity.revenue}</div>
                    <Badge variant={activity.profitMargin > 75 ? 'default' : 'secondary'}>
                      {activity.profitMargin}% margen
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground">Estudiantes</div>
                    <div className="text-xl font-bold">{activity.students}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground">Sesiones</div>
                    <div className="text-xl font-bold">{activity.sessions}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground">€/Estudiante</div>
                    <div className="text-xl font-bold">€{activity.revenuePerStudent}</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-sm text-muted-foreground">€/Sesión</div>
                    <div className="text-xl font-bold">€{activity.revenuePerSession}</div>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Ocupación</span>
                    <span className="text-sm font-medium">{activity.occupancyRate}%</span>
                  </div>
                  <Progress value={activity.occupancyRate} className="h-2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tabla de comparación */}
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            Comparación de Rentabilidad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium">Actividad</th>
                  <th className="text-right py-2 px-3 font-medium">Ingresos</th>
                  <th className="text-center py-2 px-3 font-medium">Estudiantes</th>
                  <th className="text-center py-2 px-3 font-medium">Ocupación</th>
                  <th className="text-right py-2 px-3 font-medium">Margen</th>
                  <th className="text-center py-2 px-3 font-medium">Tendencia</th>
                  <th className="text-center py-2 px-3 font-medium">Ranking</th>
                </tr>
              </thead>
              <tbody>
                {activityData
                  .sort((a, b) => b.revenue - a.revenue)
                  .map((activity, index) => (
                    <tr key={activity.name} className="border-b hover:bg-muted/50">
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <Activity className="w-4 h-4 text-primary" />
                          <span className="font-medium">{activity.name}</span>
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right font-medium">
                        €{activity.revenue.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-center">
                        <Badge variant="outline">{activity.students}</Badge>
                      </td>
                      <td className="py-2 px-3 text-center">
                        <Badge variant={activity.occupancyRate > 70 ? 'default' : 'secondary'}>
                          {activity.occupancyRate}%
                        </Badge>
                      </td>
                      <td className="py-2 px-3 text-right font-medium">
                        <span className={activity.profitMargin > 75 ? 'text-success' : 'text-warning'}>
                          {activity.profitMargin}%
                        </span>
                      </td>
                      <td className="py-2 px-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {activity.trend === 'up' ? (
                            <TrendingUp className="w-4 h-4 text-success" />
                          ) : activity.trend === 'down' ? (
                            <TrendingDown className="w-4 h-4 text-danger" />
                          ) : (
                            <Activity className="w-4 h-4 text-gray-500" />
                          )}
                          <span className={`text-sm ${
                            activity.trend === 'up' ? 'text-success' : 
                            activity.trend === 'down' ? 'text-danger' : 
                            'text-gray-500'
                          }`}>
                            {activity.growth >= 0 ? '+' : ''}{activity.growth}%
                          </span>
                        </div>
                      </td>
                      <td className="py-2 px-3 text-center">
                        <div className="flex items-center justify-center">
                          {index === 0 && <Star className="w-4 h-4 text-warning" />}
                          <span className="ml-1">#{index + 1}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Recomendaciones */}
      <Card className="shadow-card border-info/20 bg-info/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-info" />
            Recomendaciones de Rentabilidad
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-start gap-2">
              <TrendingUp className="w-4 h-4 text-success mt-0.5" />
              <div>
                <span className="font-medium">Fútbol y Padel</span> muestran el mejor crecimiento. 
                Considera ampliar horarios o capacidad.
              </div>
            </div>
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-warning mt-0.5" />
              <div>
                <span className="font-medium">Natación</span> tiene baja ocupación (50%). 
                Implementa estrategias de marketing o revisa precios.
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Target className="w-4 h-4 text-primary mt-0.5" />
              <div>
                <span className="font-medium">Tenis</span> tiene el margen más alto (85%). 
                Modelo a replicar en otras actividades.
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityProfitability;